import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Divider,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Settings24Regular,
  ClipboardTaskListLtr24Regular,
  People24Regular,
  VehicleTruckProfile24Regular,
  CodeBlock24Regular,
  TaskListSquareLtr24Regular,
  Wrench24Regular,
  DocumentText24Regular,
  Box24Regular,
  Chat24Regular,
  Calculator24Regular,
  History24Regular,
  Cloud24Regular,
  PersonAccounts24Regular,
} from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useProxyRole, ProxyRole } from '../contexts/ProxyRoleContext';
import { canSeeNavPath } from '../config/roleAccess';
import { getSidebarConfigForRoute } from '../config/sidebar-config';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  drawer: {
    width: '85vw',
    maxWidth: '320px',
  },
  body: {
    ...shorthands.padding('0'),
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
  },
  groupLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('12px', '20px', '6px'),
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '12px',
    width: '100%',
    minHeight: '48px',
    ...shorthands.padding('10px', '20px'),
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    fontSize: '15px',
    fontWeight: 500,
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  itemActive: {
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  subItem: {
    paddingLeft: '52px',
    fontSize: '14px',
    fontWeight: 400,
    minHeight: '40px',
  },
});

interface NavLink {
  icon: any;
  path: string;
  label: string;
  openInNewTab?: boolean;
}

function buildNavGroups(role: ProxyRole): { label: string; items: NavLink[] }[] {
  // Visibility is decided per-item by the shared canSeeNavPath() so this drawer
  // stays in lockstep with the rail and the route guard (config/roleAccess).
  const canSee = (path: string) => canSeeNavPath(role, path);

  const primary: NavLink[] = [
    { icon: People24Regular, path: '/customers', label: 'Customers' },
    { icon: ClipboardTaskListLtr24Regular, path: '/orders', label: 'Orders' },
    { icon: VehicleTruckProfile24Regular, path: '/loadboard', label: 'Loadboard' },
    { icon: Box24Regular, path: '/product_groups', label: 'Inventory' },
  ].filter((item) => canSee(item.path));

  const more: NavLink[] = [
    { icon: Chat24Regular, path: '/messaging', label: 'Messaging' },
    { icon: TaskListSquareLtr24Regular, path: '/tasks', label: 'Tasks' },
    { icon: Wrench24Regular, path: '/maintenance', label: 'Maintenance' },
    { icon: DocumentText24Regular, path: '/documents', label: 'Documents' },
    { icon: Calculator24Regular, path: '/financial', label: 'Financial' },
    { icon: History24Regular, path: '/activity', label: 'Activity' },
    { icon: Cloud24Regular, path: '/website', label: 'Website' },
    { icon: PersonAccounts24Regular, path: '/portal', label: 'Customer Portal', openInNewTab: true },
  ].filter((item) => canSee(item.path));

  const system: NavLink[] = [
    { icon: CodeBlock24Regular, path: '/dev', label: 'Dev' },
    { icon: Settings24Regular, path: '/settings', label: 'Settings' },
  ].filter((item) => canSee(item.path));

  const groups: { label: string; items: NavLink[] }[] = [];
  if (primary.length) groups.push({ label: 'Main', items: primary });
  if (more.length) groups.push({ label: 'More', items: more });
  if (system.length) groups.push({ label: 'System', items: system });
  return groups;
}

export default function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();

  const effectiveRole: ProxyRole = proxyRole ?? user?.role ?? 'office';
  const groups = buildNavGroups(effectiveRole);
  const sidebarConfig = getSidebarConfigForRoute(location.pathname);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const go = (path: string, openInNewTab?: boolean) => {
    if (openInNewTab) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    onClose();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(_, data) => { if (!data.open) onClose(); }}
      position="start"
      type="overlay"
      className={styles.drawer}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close navigation"
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          }
        >
          Navigation
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        {groups.map((group, gi) => (
          <div key={group.label} className={styles.group}>
            {gi > 0 && <Divider />}
            <Text className={styles.groupLabel}>{group.label}</Text>
            {group.items.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  className={mergeClasses(styles.item, isActive(item.path) && styles.itemActive)}
                  onClick={() => go(item.path, item.openInNewTab)}
                >
                  <Icon />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}

        {sidebarConfig && (
          <div className={styles.group}>
            <Divider />
            <Text className={styles.groupLabel}>This section</Text>
            {sidebarConfig.map(configItem => {
              if (configItem.type === 'category') {
                return (
                  <div key={configItem.value}>
                    {configItem.path ? (
                      <button
                        className={mergeClasses(styles.item, isActive(configItem.path) && styles.itemActive)}
                        onClick={() => go(configItem.path!)}
                      >
                        <span>{configItem.label}</span>
                      </button>
                    ) : (
                      <Text className={styles.groupLabel}>{configItem.label}</Text>
                    )}
                    {configItem.items.map(sub => (
                      <button
                        key={sub.value}
                        className={mergeClasses(
                          styles.item,
                          styles.subItem,
                          isActive(sub.path) && styles.itemActive,
                        )}
                        onClick={() => go(sub.path)}
                      >
                        <span>{sub.label}</span>
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <button
                  key={configItem.value}
                  className={mergeClasses(styles.item, isActive(configItem.path) && styles.itemActive)}
                  onClick={() => go(configItem.path)}
                >
                  <span>{configItem.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
}
